import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { ElectricityPrice, PriceArea } from '../types';
import { format, subDays, addDays } from 'date-fns';
import { fetchElectricityPrices } from '../utils/api';
import { LineChart, TrendingUp, TrendingDown } from 'lucide-react';

interface WeeklyTrendProps {
  area: PriceArea;
}

const WeeklyTrend: React.FC<WeeklyTrendProps> = ({ area }) => {
  const [weeklyData, setWeeklyData] = useState<ElectricityPrice[][]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTrend, setShowTrend] = useState(true);

  useEffect(() => {
    const fetchWeeklyData = async () => {
      setLoading(true);
      setError(null);
      try {
        const today = new Date();
        const days = [-3, -2, -1, 0, 1]; // Past 3 days, today, and tomorrow
        
        const promises = days.map(offset => {
          const date = offset < 0 ? subDays(today, Math.abs(offset)) : addDays(today, offset);
          return fetchElectricityPrices(date, area);
        });
        
        const results = await Promise.all(promises);
        setWeeklyData(results);
      } catch (err) {
        setError('Kunne ikke hente ukentlige data.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchWeeklyData();
  }, [area]);

  if (loading) {
    return (
      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm p-6 mb-6 dark:border dark:border-dark-border">
        <div className="flex items-center mb-4">
          <div className="bg-indigo-100 dark:bg-indigo-800 p-2 rounded-md mr-3">
            <LineChart className="h-5 w-5 text-indigo-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Ukentlig pristrend</h3>
        </div>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  if (error || weeklyData.length === 0) {
    return null;
  }

  // Process data for the chart
  const dailyAverages = weeklyData.map(dayData => {
    if (dayData.length === 0) return null;
    const sum = dayData.reduce((acc, price) => acc + price.NOK_per_kWh, 0);
    return sum / dayData.length;
  }).filter(Boolean) as number[];

  // Calculate trend
  const dayLabels = [-3, -2, -1, 0, 1].map(offset => {
    const date = offset < 0 ? subDays(new Date(), Math.abs(offset)) : addDays(new Date(), offset);
    return format(date, 'EEE dd.MM');
  });

  // Calculate if the trend is going up or down
  const trendDirection = dailyAverages.length >= 2 
    ? dailyAverages[dailyAverages.length - 1] > dailyAverages[dailyAverages.length - 2] 
    : null;

  // Calculate percentage change
  const percentageChange = dailyAverages.length >= 2 
    ? ((dailyAverages[dailyAverages.length - 1] - dailyAverages[dailyAverages.length - 2]) / 
       dailyAverages[dailyAverages.length - 2] * 100)
    : null;

  const chartData = {
    labels: dayLabels.slice(0, dailyAverages.length),
    datasets: [
      {
        label: 'Gjennomsnittlig strømpris',
        data: dailyAverages,
        fill: true,
        backgroundColor: 'rgba(79, 70, 229, 0.1)',
        borderColor: 'rgba(79, 70, 229, 0.8)',
        tension: 0.4,
        pointBackgroundColor: 'rgba(79, 70, 229, 1)',
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        grid: {
          color: document.documentElement.classList.contains('dark') ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
        },
        ticks: {
          color: document.documentElement.classList.contains('dark') ? '#9ca3af' : undefined
        }
      },
      x: {
        grid: {
          color: document.documentElement.classList.contains('dark') ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
        },
        ticks: {
          color: document.documentElement.classList.contains('dark') ? '#9ca3af' : undefined
        }
      }
    },
    plugins: {
      legend: {
        display: false,
        labels: {
          color: document.documentElement.classList.contains('dark') ? '#9ca3af' : undefined
        }
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            return `${context.parsed.y.toFixed(2)} NOK/kWh`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: false,
        ticks: {
          callback: function(value: any) {
            return value.toFixed(2) + ' kr';
          }
        }
      },
    },
  };

  return (
    <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm p-6 mb-6 dark:border dark:border-dark-border">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center">
          <div className="bg-indigo-100 dark:bg-indigo-800 p-2 rounded-md mr-3">
            <LineChart className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Ukentlig pristrend</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Gjennomsnittspriser de siste dagene</p>
          </div>
        </div>
        <button
          onClick={() => setShowTrend(!showTrend)}
          className="text-xs px-3 py-1 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/50"
        >
          {showTrend ? 'Skjul trend' : 'Vis trend'}
        </button>
      </div>

      {showTrend && (
        <>
          <div className="h-64 mb-4">
            <Line data={chartData} options={chartOptions} />
          </div>
          
          {percentageChange !== null && (
            <div className={`p-3 rounded-lg ${trendDirection 
              ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800' 
              : 'bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800'}`}
            >
              <div className="flex items-center">
                {trendDirection ? (
                  <>
                    <TrendingUp className={`h-5 w-5 mr-2 text-amber-500`} />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">Strømprisen er på vei opp</p>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Dagens gjennomsnittspris er <span className="font-medium text-amber-600">{Math.abs(percentageChange).toFixed(1)}% høyere</span> enn gårsdagens pris
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <TrendingDown className={`h-5 w-5 mr-2 text-green-500`} />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">Strømprisen er på vei ned</p>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Dagens gjennomsnittspris er <span className="font-medium text-green-600">{Math.abs(percentageChange).toFixed(1)}% lavere</span> enn gårsdagens pris
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WeeklyTrend;